import React, { FC, useRef, useEffect, useState } from 'react'
import { css, styled } from 'styled-components'
import { Header3 } from '..'
import CloseIconSvg from '@tuhi/components/assets/images/closeIcon.svg'
import { BaseButton } from '../../components'
import { MODAL_TITLE_ID } from '../../components/modal/globalModal'

type WidthType = 'small' | 'medium' | 'large' | 'hug'

const widthStyles = {
    small: css`
        width: 468px;
    `,
    medium: css`
        width: 530px;
    `,
    large: css`
        width: 560px;
    `,
    hug: css`
        width: auto;
    `,
}

const Container = styled.div<{ $width: WidthType }>`
    display: flex;
    flex-direction: column;
    background-color: ${(p) => p.theme.colors.white};
    border-radius: 20px;
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.25);
    padding: 24px 32px;
    gap: 15px;

    ${(p) => widthStyles[p.$width]}
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 16px;
`

const CloseButton = styled(BaseButton)`
    width: 24px;
    height: 24px;
`

/** Wrapper that smoothly animates height when children change size */
const AnimatedContent = styled.div<{ $height: number | null }>`
    overflow-y: auto;
    overflow-x: hidden;
    transition: height 250ms ease-out;
    max-height: 75vh;
    ${(p) =>
        p.$height !== null &&
        css`
            height: ${p.$height}px;
        `}
`

interface Props {
    title: string
    closeButtonOnClick: () => void
    children: React.ReactNode
    width: WidthType
    className?: string
}

interface LoadingProps {
    title: string
    children: React.ReactNode
    width: WidthType
    className?: string
}

export const BaseModal: FC<Props> = ({
    title,
    children,
    closeButtonOnClick,
    width,
    className,
}) => {
    const contentRef = useRef<HTMLDivElement>(null)
    const [height, setHeight] = useState<number | null>(null)

    useEffect(() => {
        const el = contentRef.current
        if (!el) return

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setHeight(entry.contentRect.height)
            }
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <Container className={className} $width={width}>
            <Header>
                <Header3 id={MODAL_TITLE_ID}>{title}</Header3>
                <CloseButton
                    type="button"
                    aria-label="Close dialog"
                    onClick={closeButtonOnClick}
                >
                    <CloseIconSvg aria-hidden="true" focusable="false" />
                </CloseButton>
            </Header>
            <AnimatedContent $height={height}>
                <div ref={contentRef}>{children}</div>
            </AnimatedContent>
        </Container>
    )
}

export const LoadingModal: FC<LoadingProps> = ({
    title,
    children,
    width,
    className,
}) => {
    return (
        <Container className={className} $width={width}>
            <Header>
                <Header3>{title}</Header3>
            </Header>
            {children}
        </Container>
    )
}
